import * as THREE from "three";
import type { AssetPipeline } from "../core/assets";
import { addHorizonRing } from "./horizon";
import { demSample } from "./map02-heightmap";
import { RIVER_VALLEY_ROUTE } from "./nav";
import { addMap02Places } from "./places";
import { STYLIZED_WORLD } from "./style";
import { setTerrainHeightProvider } from "./terrain";
import { makeSplatGroundMaterial, paintFarFieldVertexColors, TERRAIN_MATERIALS as MAT } from "./terrain-materials";

const ROUTE = RIVER_VALLEY_ROUTE.map((node) => node.position);

const FOAM = new THREE.MeshBasicMaterial({ color: 0xcfd8d2, transparent: true, opacity: 0.3, depthWrite: false, side: THREE.DoubleSide });
const textureLoader = new THREE.TextureLoader();

function loadMapTexture(path: string, color = false, repeatX = 1, repeatY = 1): THREE.Texture {
  if (typeof document === "undefined") return new THREE.Texture();
  const texture = textureLoader.load(path);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  if (color) texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

const ROAD_COLOR = loadMapTexture("/textures/world/Road006_1K-JPG_Color.jpg", true, 18, 2);
const ROAD_NORMAL = loadMapTexture("/textures/world/Road006_1K-JPG_NormalGL.jpg", false, 18, 2);
const ROAD_ROUGHNESS = loadMapTexture("/textures/world/Road006_1K-JPG_Roughness.jpg", false, 18, 2);

// Arid set (Poly Haven CC0): dry cracked soil base, aerial dirt for scrub
// breakup, drone-scanned scree for slopes.
const DRY_COLOR = loadMapTexture("/textures/ground/dry_ground_01/dry_ground_01_diff_1k.jpg", true, 26, 26);
const DRY_NORMAL = loadMapTexture("/textures/ground/dry_ground_01/dry_ground_01_nor_gl_1k.jpg", false, 26, 26);
const DRY_ROUGHNESS = loadMapTexture("/textures/ground/dry_ground_01/dry_ground_01_rough_1k.jpg", false, 26, 26);
const SCRUB_COLOR = loadMapTexture("/textures/ground/dirt_aerial_02/dirt_aerial_02_diff_1k.jpg", true);
const SCREE_COLOR = loadMapTexture("/textures/ground/rocky_terrain_02/rocky_terrain_02_diff_1k.jpg", true);

const MAP02_GROUND = STYLIZED_WORLD
  ? new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true, // faceted triangles — the stylized terrain read
      roughness: 1,
      metalness: 0,
    })
  : makeSplatGroundMaterial({
      base: { map: DRY_COLOR, normalMap: DRY_NORMAL, roughnessMap: DRY_ROUGHNESS, normalScale: 0.34 },
      splatX: { map: SCRUB_COLOR, scale: 0.05 }, // scrub patches + wet sediment band (tinted dark)
      splatY: { map: SCREE_COLOR, scale: 0.042 }, // slope scree
    });

const ROAD_MATERIAL = STYLIZED_WORLD
  ? new THREE.MeshStandardMaterial({
      color: 0x554f40, // flat packed-earth band; palette does the work
      roughness: 0.96,
      metalness: 0,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -2,
    })
  : new THREE.MeshStandardMaterial({
      color: 0x9f9178,
      map: ROAD_COLOR,
      normalMap: ROAD_NORMAL,
      roughnessMap: ROAD_ROUGHNESS,
      roughness: 0.95,
      metalness: 0,
      normalScale: new THREE.Vector2(0.2, 0.2),
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -2,
    });

function smoothNoise(x: number, z: number, scale: number): number {
  return (
    Math.sin(x * scale) * 0.42 +
    Math.cos(z * scale * 1.17) * 0.36 +
    Math.sin((x + z) * scale * 0.63) * 0.22
  ) * 0.5 + 0.5;
}

function distanceToSegment(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): { distance: number; t: number } {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq <= 1e-6 ? 0 : THREE.MathUtils.clamp(((px - ax) * abx + (pz - az) * abz) / lenSq, 0, 1);
  const cx = ax + abx * t;
  const cz = az + abz * t;
  return { distance: Math.hypot(px - cx, pz - cz), t };
}

function distanceToRoute(x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i < ROUTE.length - 1; i++) {
    const a = ROUTE[i]!;
    const b = ROUTE[i + 1]!;
    best = Math.min(best, distanceToSegment(x, z, a.x, a.z, b.x, b.z).distance);
  }
  return best;
}

// All drivable roads: the main route plus the authored spurs. Building
// placement and road-dust paint must respect every one of them, not just the
// squad route (buildings were spawning on the spur roads).
const TOWN_SPUR: THREE.Vector3[] = [
  new THREE.Vector3(-275, 0, 120),
  new THREE.Vector3(-215, 0, 185),
  new THREE.Vector3(-120, 0, 245),
];
const BASE_SPUR: THREE.Vector3[] = [
  new THREE.Vector3(-675, 0, -455),
  new THREE.Vector3(-620, 0, -410),
  new THREE.Vector3(-560, 0, -370),
];

function distanceToAnyRoad(x: number, z: number): number {
  let best = distanceToRoute(x, z);
  for (const spur of [TOWN_SPUR, BASE_SPUR]) {
    for (let i = 0; i < spur.length - 1; i++) {
      const a = spur[i]!;
      const b = spur[i + 1]!;
      best = Math.min(best, distanceToSegment(x, z, a.x, a.z, b.x, b.z).distance);
    }
  }
  return best;
}

function riverCenterX(z: number): number {
  return -235 + z * 0.5 + Math.sin(z * 0.0055) * 72;
}

export function map02RiverDistanceAt(x: number, z: number): number {
  const main = Math.abs(x - riverCenterX(z));
  const forkT = THREE.MathUtils.clamp((z - 20) / 360, 0, 1);
  const forkCenter = riverCenterX(z) + forkT * (150 + z * 0.1);
  const fork = z > 20 ? Math.abs(x - forkCenter) : Infinity;
  return Math.min(main, fork);
}

// Macro relief comes from a real SRTM sample of the Kunar valley (see
// scripts/fetch-dem.py) — eroded ridgelines, gullies, and drainage that
// procedural noise never produced. Scaled so walls stay readable from orbit.
const DEM_EXTENT = 1250; // world units mapped to the DEM's u/v 0..1 span
const DEM_RELIEF = 165; // game height of the full DEM elevation range
const DEM_FLOOR = 0.16; // normalized DEM value pinned to the valley floor

function demHeightAt(x: number, z: number): number {
  const u = x / (DEM_EXTENT * 2) + 0.5;
  const v = z / (DEM_EXTENT * 2) + 0.5;
  return (demSample(u, v) - DEM_FLOOR) * DEM_RELIEF;
}

// River channel profile: a wet sediment shelf, then a sharp cut bank down to
// the bed. The old single smoothstep read as a soft dip indistinguishable
// from the surrounding land.
export const RIVER_BED_Y = -6.5;
export const RIVER_SHELF_Y = -1.4;
export const RIVER_WATER_Y = -3.3; // floods most of the bed, sits under the shelf lip

export function map02HeightAt(x: number, z: number): number {
  const routeDist = distanceToRoute(x, z);
  // Relief amplification with distance from the corridor: the mission space
  // keeps readable walls, while the map edges rise into dramatic peaks
  // (notorious-Afghan-valley energy) tall enough to carry a snowline.
  const edgeAmp = 1 + THREE.MathUtils.smoothstep(routeDist, 180, 620) * 3.2;
  // Clamp side-drainage bowls that dip below our valley-floor reference —
  // unclamped they fall under the far-field disc, which then reads as a
  // pale sea poking through the terrain.
  let height = Math.max(demHeightAt(x, z) * edgeAmp, -1.2);
  // High-frequency detail the ~30m DEM can't carry, slopes only
  height += (smoothNoise(x, z, 0.05) - 0.5) * 3.6 * THREE.MathUtils.smoothstep(height, 4, 60);

  // The route corridor is gameplay truth: blend the real relief out to a
  // gently undulating floor so the mission space stays walkable and readable.
  const corridorMask = 1 - THREE.MathUtils.smoothstep(routeDist, 36, 240);
  const floor = 1.6 + (smoothNoise(x + 40, z - 25, 0.011) - 0.5) * 4.2;
  height = THREE.MathUtils.lerp(height, floor, corridorMask * 0.96);

  const rDist = map02RiverDistanceAt(x, z);
  const shelf = 1 - THREE.MathUtils.smoothstep(rDist, 34, 78);
  const drop = 1 - THREE.MathUtils.smoothstep(rDist, 21, 34);
  height = THREE.MathUtils.lerp(height, RIVER_SHELF_Y, shelf);
  height = THREE.MathUtils.lerp(height, RIVER_BED_Y, drop);

  // Fade to the far-field level at the plane boundary — amplified peaks
  // would otherwise end in a sheer cliff at the map edge.
  const edgeDist = Math.max(Math.abs(x), Math.abs(z));
  const edgeFade = 1 - THREE.MathUtils.smoothstep(edgeDist, 990, 1092);
  return THREE.MathUtils.lerp(-2, height, edgeFade);
}

// Stylized paint: hard-ish altitude/slope bands with noise-dithered edges —
// the palette carries everything (no textures). Bands chosen for the sensor
// aesthetic: readable zones, warm floor, cool high rock.
function paintMap02GroundStylized(geo: THREE.PlaneGeometry): void {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const norm = geo.attributes["normal"] as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const color = new THREE.Color();

  // John's gradient: tan valley floor, browns darkening with altitude,
  // icy white-grey on the peaks.
  const BED = new THREE.Color(0x44503f);
  const FLOOR = new THREE.Color(0xbd9c66);
  const SCRUB = new THREE.Color(0x8b8257); // dry olive-tan, not golf green
  const BROWN_MID = new THREE.Color(0x6f5540);
  const BROWN_HIGH = new THREE.Color(0x4a3a2c);
  const ROCK_COLD = new THREE.Color(0x6e675f); // transition under the snowline
  const SNOW = new THREE.Color(0xe6e9ea);
  const DUST = new THREE.Color(0xc7ab74);

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const slope = 1 - norm.getY(i);
    const dither = (smoothNoise(x, z, 0.028) - 0.5) * 9; // breaks band lines
    const h = y + dither;

    if (h < 8) color.copy(FLOOR);
    else if (h < 34) {
      // Patchy scrub — a solid band reads as a golf course from altitude
      const patch = smoothNoise(x + 130, z - 70, 0.012);
      color.copy(FLOOR).lerp(SCRUB, 0.2 + patch * 0.5);
    } else if (h < 100) color.copy(BROWN_MID);
    else if (h < 155) color.copy(BROWN_HIGH);
    else if (h < 195) color.copy(ROCK_COLD);
    else color.copy(SNOW);

    // Steep faces read as bare rock; snow doesn't cling to them
    if (slope > 0.34 && h > 6) color.lerp(h > 180 ? ROCK_COLD : BROWN_HIGH, 0.7);

    // Wet sediment band around the channel — tight, dark, decisive
    const rDist = map02RiverDistanceAt(x, z);
    const wetBand = 1 - THREE.MathUtils.smoothstep(rDist, 30, 56);
    color.lerp(BED, wetBand);

    // Soft dust apron under the road ribbons (the crisp line is the ribbon
    // mesh; a hard painted band at 7-unit vertex resolution is a sawtooth)
    const roadDist = distanceToAnyRoad(x, z);
    const apron = 1 - THREE.MathUtils.smoothstep(roadDist, 8, 32);
    color.lerp(DUST, apron * 0.5);

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
}

function paintMap02Ground(geo: THREE.PlaneGeometry): void {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const norm = geo.attributes["normal"] as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const splats = new Float32Array(pos.count * 3);
  const color = new THREE.Color();
  const sand = new THREE.Color(0xa8916c);
  const dry = new THREE.Color(0x85745c);
  const rock = new THREE.Color(0x7a7266);
  const wetSediment = new THREE.Color(0x413f36);
  const road = new THREE.Color(0x9c8b70);

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const slope = 1 - norm.getY(i);
    const route = 1 - THREE.MathUtils.smoothstep(distanceToAnyRoad(x, z), 20, 68);
    const rDist = map02RiverDistanceAt(x, z);
    // Wet band covers the carved shelf and cut bank, ending at the lip
    const wetBand = 1 - THREE.MathUtils.smoothstep(rDist, 32, 60);
    const ridge = THREE.MathUtils.clamp(y / 90, 0, 1);
    const breakup = smoothNoise(x, z, 0.019);
    const scrubPatch = Math.max(0, smoothNoise(x - 60, z + 90, 0.008) - 0.62) * 1.2;

    color.copy(sand)
      .lerp(dry, breakup * 0.5)
      .lerp(rock, THREE.MathUtils.clamp(ridge * 0.5 + slope * 1.6, 0, 0.85));
    // Lift the natural tint toward unity (texture carries brightness), then
    // apply wear/wetness dark so they keep contrast.
    color.multiplyScalar(1.75);
    color.lerp(road, route * 0.65);
    color.lerp(wetSediment, wetBand * 0.85);
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;

    // Splat weights: x = scrub/sediment texture (wet band forces it so the
    // channel reads as gravel sediment), y = scree on slopes and high ridges,
    // z = dry soil elsewhere.
    const scrubW = Math.min(1.6, scrubPatch * (1 - slope * 2) + wetBand * 1.8);
    const screeW = THREE.MathUtils.clamp(slope * 5.2, 0, 1.4) + ridge * 0.7;
    const soilW = 0.55 + route * 0.9;
    splats[i * 3] = Math.max(0, scrubW);
    splats[i * 3 + 1] = Math.max(0, screeW * (1 - wetBand));
    splats[i * 3 + 2] = soilW * (1 - wetBand * 0.7);
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geo.setAttribute("splat", new THREE.BufferAttribute(splats, 3));
}

function makeRiverRibbon(name: string, points: THREE.Vector3[], width: number, y: number, material: THREE.Material): THREE.Mesh {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const p = points[i]!;
    const leftX = p.x - right.x * width * 0.5;
    const leftZ = p.z - right.z * width * 0.5;
    const rightX = p.x + right.x * width * 0.5;
    const rightZ = p.z + right.z * width * 0.5;
    positions.push(leftX, map02HeightAt(leftX, leftZ) + y, leftZ);
    positions.push(rightX, map02HeightAt(rightX, rightZ) + y, rightZ);
    if (i < points.length - 1) {
      const base = i * 2;
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3); // upward-facing winding
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const mesh = new THREE.Mesh(geo, material);
  mesh.name = name;
  mesh.receiveShadow = true;
  mesh.userData["losTransparent"] = true; // conformal ribbon — never occludes infantry LOS
  return mesh;
}

function makeWaterNormal(seed: number): THREE.DataTexture {
  const size = 128;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const sx = x / size;
      const sy = y / size;
      const waveA = Math.sin((sx * 18 + sy * 9 + seed) * Math.PI * 2);
      const waveB = Math.cos((sx * -7 + sy * 21 + seed * 0.37) * Math.PI * 2);
      const nx = 128 + waveA * 34 + waveB * 18;
      const ny = 128 + waveB * 32 - waveA * 12;
      const i = (y * size + x) * 4;
      data[i] = THREE.MathUtils.clamp(nx, 0, 255);
      data[i + 1] = THREE.MathUtils.clamp(ny, 0, 255);
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.needsUpdate = true;
  return texture;
}

const WATER_NORMAL_A = makeWaterNormal(0.13);

// Flat water surface: constant level across the channel width (the old
// version draped the surface over the terrain-following ribbon, which is why
// the water was indistinguishable from wet ground).
function makeFlatWaterGeometry(points: THREE.Vector3[], width: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const p = points[i]!;
    positions.push(p.x - right.x * width * 0.5, RIVER_WATER_Y, p.z - right.z * width * 0.5);
    positions.push(p.x + right.x * width * 0.5, RIVER_WATER_Y, p.z + right.z * width * 0.5);
    if (i < points.length - 1) {
      const base = i * 2;
      // Winding order matters: the (base, base+1, …) order the old ribbons
      // used faces DOWN, which is why the water surface was never visible
      // from the aircraft. These face up.
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

// Dark, glossy, env-reflective sheet. Water2's clear refraction showed the
// gravel bed through the surface (and cost two reflection passes per frame);
// a dark reflective standard material is what actually reads as "river" from
// the aircraft.
// Low metalness + a lighter base blue: metal-dark water went near-black when
// viewed straight down (reflection vector hits the dark zenith), so the river
// vanished under the aircraft while reading fine at grazing angles ahead.
// Stylized variant: solid saturated teal, no normal map — a confident flat
// color band is the stylized water read.
const WATER_SURFACE = STYLIZED_WORLD
  ? new THREE.MeshStandardMaterial({
      color: 0x2f7fa6,
      roughness: 0.4,
      metalness: 0,
      envMapIntensity: 0.7,
    })
  : new THREE.MeshStandardMaterial({
      color: 0x1d5070,
      roughness: 0.22,
      metalness: 0.05,
      normalMap: WATER_NORMAL_A,
      normalScale: new THREE.Vector2(0.35, 0.35),
      envMapIntensity: 1.0,
    });
WATER_SURFACE.normalMap?.repeat.set(60, 60);

function makeWaterRibbon(name: string, points: THREE.Vector3[], width: number): THREE.Mesh {
  const water = new THREE.Mesh(makeFlatWaterGeometry(points, width), WATER_SURFACE);
  water.name = name;
  water.receiveShadow = true;
  water.userData["losTransparent"] = true;
  return water;
}

// Invisible solid strips along the channel: fed to navmesh generation (via
// the navObstacle tag) so the riverbed voxelizes as blocked and agents can
// only cross at the bridge. Never rendered, never raycast for shots or LOS.
// The volumes must rise WELL above the banks — a top surface near bank height
// is itself walkable to recast, which turned the first version into a
// river-wide footbridge (enemies walked on water). Segments near the bridge
// are skipped so the deck stays the one legal crossing.
const BRIDGE_CROSSING_Z = 40;
function addRiverNavObstacles(parent: THREE.Group, points: THREE.Vector3[], width: number, gapAtBridge: boolean): void {
  const mat = new THREE.MeshBasicMaterial();
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const midZ = (a.z + b.z) / 2;
    if (gapAtBridge && Math.abs(midZ - BRIDGE_CROSSING_Z) < 24) continue;
    const length = Math.hypot(b.x - a.x, b.z - a.z) + 6;
    const box = new THREE.Mesh(new THREE.BoxGeometry(width, 20, length), mat);
    box.position.set((a.x + b.x) / 2, RIVER_BED_Y + 10, (a.z + b.z) / 2); // top ~+13.5: unclimbable from the banks
    box.rotation.y = Math.atan2(b.x - a.x, b.z - a.z);
    box.visible = false;
    box.userData["navObstacle"] = true;
    box.userData["ignoreBallistics"] = true;
    box.userData["losTransparent"] = true;
    parent.add(box);
  }
}

function makeEdgeRibbon(name: string, points: THREE.Vector3[], offset: number): THREE.Mesh {
  const edgePoints = points.map((p, i) => {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const x = p.x + right.x * offset;
    const z = p.z + right.z * offset;
    return new THREE.Vector3(x, 0, z);
  });
  return makeRiverRibbon(name, edgePoints, 3.5, 1.08, FOAM);
}

function makeRoadRibbon(name: string, points: THREE.Vector3[], width: number): THREE.Mesh {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  let distanceAlong = 0;
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    if (i > 0) distanceAlong += points[i]!.distanceTo(points[i - 1]!);
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const p = points[i]!;
    const leftX = p.x - right.x * width * 0.5;
    const leftZ = p.z - right.z * width * 0.5;
    const rightX = p.x + right.x * width * 0.5;
    const rightZ = p.z + right.z * width * 0.5;
    positions.push(leftX, map02HeightAt(leftX, leftZ) + 0.18, leftZ);
    positions.push(rightX, map02HeightAt(rightX, rightZ) + 0.18, rightZ);
    uvs.push(0, distanceAlong / 70, 1, distanceAlong / 70);
    if (i < points.length - 1) {
      const base = i * 2;
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3); // upward-facing winding
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const road = new THREE.Mesh(geo, ROAD_MATERIAL);
  road.name = name;
  road.receiveShadow = true;
  road.userData["losTransparent"] = true;
  return road;
}

function addRiver(parent: THREE.Group): void {
  const main: THREE.Vector3[] = [];
  for (let z = -1050; z <= 980; z += 44) {
    main.push(new THREE.Vector3(riverCenterX(z), 0, z));
  }
  const fork: THREE.Vector3[] = [];
  for (let z = 20; z <= 900; z += 44) {
    const t = THREE.MathUtils.clamp((z - 20) / 360, 0, 1);
    fork.push(new THREE.Vector3(riverCenterX(z) + t * (150 + z * 0.1), 0, z));
  }
  // Water + foam only — the old painted bank/wetbank ribbons are retired;
  // the carved shelf and the ground shader own the bank read now.
  parent.add(makeWaterRibbon("map02-river-main", main, 46));
  parent.add(makeEdgeRibbon("map02-river-edge-main-l", main, -25));
  parent.add(makeEdgeRibbon("map02-river-edge-main-r", main, 25));
  addRiverNavObstacles(parent, main, 52, true); // gap at the bridge crossing
  parent.add(makeWaterRibbon("map02-river-fork", fork, 38));
  parent.add(makeEdgeRibbon("map02-river-edge-fork-l", fork, -21));
  parent.add(makeEdgeRibbon("map02-river-edge-fork-r", fork, 21));
  addRiverNavObstacles(parent, fork, 44, false); // fork has no crossing
}

// Roads and ribbons must sample the ground densely — a quad strip with
// vertices only at the sparse route points floats and buries over undulating
// terrain (why the road "looked off").
function resamplePolyline(points: THREE.Vector3[], step: number): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const length = a.distanceTo(b);
    const segments = Math.max(1, Math.ceil(length / step));
    for (let s = 0; s < segments; s++) {
      out.push(a.clone().lerp(b, s / segments));
    }
  }
  out.push(points[points.length - 1]!.clone());
  return out;
}

function addRoads(parent: THREE.Group): void {
  parent.add(makeRoadRibbon("map02-main-route-road", resamplePolyline(ROUTE, 12), 18));
  parent.add(makeRoadRibbon("map02-town-spur-road", resamplePolyline(TOWN_SPUR, 12), 14));
  parent.add(makeRoadRibbon("map02-base-spur-road", resamplePolyline(BASE_SPUR, 12), 16));
}

// --- Instanced scatter -----------------------------------------------------
// Low-poly rocks and scrub clumps break the empty ground plane between the
// authored places. Two InstancedMeshes (one draw call each), deterministic
// placement, banded by the same altitude logic as the paint so clusters sit
// where the palette says they should. Pure dressing: no LOS, no ballistics,
// no navigation.

function scatterRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    return (s >>> 0) / 0xffffffff;
  };
}

function slopeAt(x: number, z: number): number {
  const d = 4;
  const h0 = map02HeightAt(x, z);
  const dx = (map02HeightAt(x + d, z) - h0) / d;
  const dz = (map02HeightAt(x, z + d) - h0) / d;
  return Math.hypot(dx, dz);
}

type ScatterRule = {
  count: number;
  accept: (x: number, z: number, h: number) => boolean;
  colorFor: (h: number, rnd: () => number) => THREE.Color;
  scale: (rnd: () => number) => THREE.Vector3;
  sink: number; // fraction of height embedded in the ground
};

function buildScatter(name: string, geo: THREE.BufferGeometry, rule: ScatterRule, seed: number): THREE.InstancedMesh {
  const material = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.95, metalness: 0 });
  const mesh = new THREE.InstancedMesh(geo, material, rule.count);
  const rnd = scatterRng(seed);
  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  let placed = 0;
  let attempts = 0;
  while (placed < rule.count && attempts < rule.count * 30) {
    attempts++;
    const x = (rnd() - 0.5) * 2040;
    const z = (rnd() - 0.5) * 2040;
    if (Math.max(Math.abs(x), Math.abs(z)) > 900) continue; // edge-fade zone
    const h = map02HeightAt(x, z);
    if (!rule.accept(x, z, h)) continue;
    const s = rule.scale(rnd);
    quat.setFromAxisAngle(up, rnd() * Math.PI * 2);
    matrix.compose(new THREE.Vector3(x, h + s.y * (0.5 - rule.sink), z), quat, s);
    mesh.setMatrixAt(placed, matrix);
    mesh.setColorAt(placed, rule.colorFor(h, rnd));
    placed++;
  }
  mesh.count = placed;
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.frustumCulled = false; // instance bounds span the map
  mesh.userData["losTransparent"] = true;
  mesh.userData["ignoreBallistics"] = true;
  return mesh;
}

function addScatter(parent: THREE.Group): void {
  const rockLow = new THREE.Color(0x8f7f68);
  const rockMid = new THREE.Color(0x6f5c48);
  const rockHigh = new THREE.Color(0x716a62);
  const scrubA = new THREE.Color(0x6d7040);
  const scrubB = new THREE.Color(0x565c33);

  // Rocks favor slopes and altitude — scree fields on the walls, sparse
  // boulders on the floor.
  parent.add(buildScatter("map02-scatter-rocks", new THREE.IcosahedronGeometry(1, 0), {
    count: 1500,
    accept: (x, z, h) => {
      if (h < -1 || map02RiverDistanceAt(x, z) < 42 || distanceToAnyRoad(x, z) < 15) return false;
      const steep = slopeAt(x, z);
      // Always allowed on real slopes/heights; floor boulders are rare
      if (h > 30 || steep > 0.28) return true;
      return smoothNoise(x - 200, z + 310, 0.02) > 0.86;
    },
    colorFor: (h, rnd) => {
      const base = h > 175 ? rockHigh : h > 40 ? rockMid : rockLow;
      return base.clone().multiplyScalar(0.85 + rnd() * 0.3);
    },
    scale: (rnd) => {
      const s = 0.9 + rnd() * rnd() * 3.4;
      return new THREE.Vector3(s * (0.7 + rnd() * 0.7), s * (0.55 + rnd() * 0.5), s * (0.7 + rnd() * 0.7));
    },
    sink: 0.3,
  }, 0x5ca77e));

  // Scrub hugs the valley floor in the same noise patches the paint uses, so
  // clumps land on the painted scrub and the band reads as vegetation.
  parent.add(buildScatter("map02-scatter-scrub", new THREE.IcosahedronGeometry(1, 0), {
    count: 2200,
    accept: (x, z, h) => {
      if (h < 0.2 || h > 34) return false;
      if (map02RiverDistanceAt(x, z) < 36 || distanceToAnyRoad(x, z) < 13 || distanceToRoute(x, z) < 24) return false;
      return smoothNoise(x + 130, z - 70, 0.012) > 0.52; // match paint patches
    },
    colorFor: (_h, rnd) => scrubA.clone().lerp(scrubB, rnd()).multiplyScalar(0.9 + rnd() * 0.25),
    scale: (rnd) => {
      const s = 0.8 + rnd() * 1.6;
      return new THREE.Vector3(s * (0.9 + rnd() * 0.6), s * 0.55, s * (0.9 + rnd() * 0.6));
    },
    sink: 0.25,
  }, 0x3eaf01));
}

function addAsyncAssetSites(parent: THREE.Group, assets?: AssetPipeline): void {
  if (!assets) return;
  const placements = [
    { path: "/models/world/kenney-industrial/building-b.glb", x: -662, z: -432, s: 0.95, r: 0.55 },
    { path: "/models/world/kenney-industrial/building-h.glb", x: -610, z: -392, s: 1.05, r: 0.55 },
    { path: "/models/world/kenney-industrial/detail-tank.glb", x: -574, z: -430, s: 1.15, r: 0 },
    { path: "/models/world/kenney-industrial/building-q.glb", x: -370, z: -40, s: 0.85, r: 0.45 },
    { path: "/models/world/kenney-industrial/building-t.glb", x: -325, z: -8, s: 0.88, r: 0.42 },
    { path: "/models/world/kenney-industrial/building-a.glb", x: -220, z: 184, s: 1.1, r: -0.25 },
    { path: "/models/world/kenney-industrial/building-r.glb", x: -160, z: 244, s: 1.0, r: -0.18 },
    { path: "/models/world/kenney-industrial/building-h.glb", x: -255, z: 236, s: 0.95, r: -0.32 },
    { path: "/models/world/kenney-industrial/building-b.glb", x: -95, z: 210, s: 0.92, r: -0.16 },
    { path: "/models/world/kenney-industrial/chimney-large.glb", x: -78, z: 270, s: 1.2, r: 0.1 },
    { path: "/models/world/kenney-industrial/detail-tank.glb", x: -135, z: 390, s: 1.25, r: 0 },
    { path: "/models/world/kenney-industrial/building-q.glb", x: -85, z: 374, s: 0.9, r: 0.15 },
    { path: "/models/world/kenney-industrial/building-t.glb", x: -155, z: 298, s: 0.8, r: 0.1 },
    { path: "/models/world/kenney-industrial/detail-tank.glb", x: -530, z: -318, s: 1.0, r: 0 },
    { path: "/models/world/kenney-industrial/detail-tank.glb", x: -502, z: -292, s: 0.9, r: 0 },
    { path: "/models/world/kenney-industrial/chimney-large.glb", x: -180, z: 18, s: 0.8, r: 0 },
  ];
  for (const item of placements) {
    assets.loadSceneTemplate(item.path).then((template) => {
      const clone = assets.cloneSceneTemplate(template);
      clone.name = `map02-asset:${item.path.split("/").pop() ?? "asset"}`;
      clone.position.set(item.x, map02HeightAt(item.x, item.z), item.z);
      clone.rotation.y = item.r;
      clone.scale.setScalar(item.s);
      parent.add(clone);
    }).catch(() => {
      // Asset-backed dressing is optional; mission function should not depend on decorative loads.
    });
  }
}

export class Map02Terrain {
  readonly group = new THREE.Group();

  constructor(size = 2200, assets?: AssetPipeline) {
    setTerrainHeightProvider(map02HeightAt);
    // 300 segments ≈ 7.3 units/vertex — enough for the sharp cut banks
    const geo = new THREE.PlaneGeometry(size, size, 300, 300);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes["position"] as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, map02HeightAt(pos.getX(i), pos.getZ(i)));
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
    if (STYLIZED_WORLD) paintMap02GroundStylized(geo);
    else paintMap02Ground(geo);
    const ground = new THREE.Mesh(geo, MAP02_GROUND);
    ground.name = "map02-ground";
    ground.receiveShadow = true;
    this.group.add(ground);

    addRoads(this.group);
    addRiver(this.group);
    // Buildings keep off every road (route + spurs) and out of the river
    addMap02Places(this.group, map02HeightAt, (x, z) =>
      map02RiverDistanceAt(x, z) > 58 && distanceToAnyRoad(x, z) > 20);
    addAsyncAssetSites(this.group, assets);
    if (STYLIZED_WORLD) addScatter(this.group);
    this.addFarField(size);
    // Slightly warmer haze than map01 — this map flies at golden hour
    addHorizonRing(this.group, size, { ridge: 0x5d5a52, haze: 0xc2b3a0 }, 0x2b1d6e0);
  }

  private addFarField(size: number): void {
    const geo = new THREE.CircleGeometry(size * 1.7, 96);
    geo.rotateX(-Math.PI / 2);
    paintFarFieldVertexColors(geo, size * 0.5, STYLIZED_WORLD
      ? { inner: 0x8d7c5f, outer: 0x9a8a6e, dry: 0x7c6d54 }
      : { inner: 0x6a5c48, outer: 0x7d6f58, dry: 0x5e523f });
    const disc = new THREE.Mesh(
      geo,
      STYLIZED_WORLD
        ? new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1, metalness: 0 })
        : MAT.farField,
    );
    disc.name = "map02-far-field";
    disc.userData["ignoreBallistics"] = true;
    disc.userData["losTransparent"] = true;
    disc.receiveShadow = true;
    this.group.add(disc);
  }
}
